import Link, { LinkProps } from "next/link"
import { useRouter } from "next/router"
import { ReactNode, useMemo } from "react"

import { flaskUrl, url } from "@/lib/url"
import { useSidebarDataQuery } from "@/v2/graphql/public-generated-rq"
import { useOrgSlug } from "@/v2/hooks/router"
import { useAuth } from "@/v2/providers/Auth"
import { Drawer, twMerge } from "@lumen-energy/design-system"
import * as NavigationMenu from "@radix-ui/react-navigation-menu"
import {
  Icon,
  IconBolt,
  IconBuilding,
  IconHelpOctagon,
  IconHome,
  IconList,
  IconPaperBag,
  IconSend,
  IconSettings,
} from "@tabler/icons-react"
import { IconAdjustmentsDollar } from "@tabler/icons-react"

import { SettingsTab } from "../../../settings/Settings"

const SidebarWrapper = ({
  className,
  children,
  open,
  onClose,
}: {
  className?: string
  children: ReactNode
  open?: boolean
  onClose?: () => void
}) => {
  return (
    <Drawer.Root open={open} onOpenChange={(value) => !value && onClose?.()}>
      <Drawer.Content
        position="left"
        className="top-header block w-full max-w-md md:max-w-[280px]"
        overlayClassName="hidden"
      >
        <NavigationMenu.Root
          className={twMerge(
            "group flex min-h-page w-full flex-col bg-brand-primary-900 text-white [&>:last-child]:flex [&>:last-child]:flex-grow [&>:last-child]:flex-col",
            className,
          )}
          orientation="vertical"
        >
          <NavigationMenu.List className="flex flex-grow flex-col">
            {children}
          </NavigationMenu.List>
        </NavigationMenu.Root>
      </Drawer.Content>
    </Drawer.Root>
  )
}

export const Sidebar = ({
  className,
  open,
  onClose,
}: {
  className?: string
  open?: boolean
  onClose?: () => void
}) => {
  const router = useRouter()
  const orgSlug = useOrgSlug()
  const { isAdmin } = useAuth()
  const { data: sidebarData } = useSidebarDataQuery({ orgSlug }, { enabled: !!orgSlug })
  const orgViews = useMemo(() => {
    if (!sidebarData)
      return {
        buildingOwner: false,
        invest: false,
      }
    // Show building owner links if org has sites
    const buildingOwner = (sidebarData.orgBySlug?.sites.view.count ?? 0) > 0
    // Show invest links if org has bundles as a financier
    const invest = sidebarData.orgBySlug?.revampIsFinancier

    return {
      buildingOwner,
      invest,
    }
  }, [sidebarData])

  const content = (
    <div className="flex flex-grow flex-col py-6">
      {orgViews.buildingOwner ? (
        <>
          <MenuItem
            href={url(["/[orgSlug]", { orgSlug }])}
            isActive={router.pathname === "/[orgSlug]"}
            icon={IconHome}
          >
            Home
          </MenuItem>
          <MenuItem
            href={url(["/[orgSlug]/properties", { orgSlug }])}
            isActive={router.pathname.startsWith("/[orgSlug]/properties")}
            icon={IconBuilding}
          >
            Property hub
          </MenuItem>
        </>
      ) : null}
      {orgViews.invest ? (
        <MenuItem
          href={url(["/[orgSlug]/invest", { orgSlug }])}
          isActive={router.pathname.startsWith("/[orgSlug]/invest")}
          icon={IconAdjustmentsDollar}
        >
          Investments
        </MenuItem>
      ) : null}
      {isAdmin ? (
        <MenuItem
          href={url(["/[orgSlug]/bundles", { orgSlug }])}
          isActive={router.pathname.startsWith("/[orgSlug]/bundles")}
          icon={IconPaperBag}
        >
          Bundles
        </MenuItem>
      ) : null}
      <MenuItem
        href={url(["/[orgSlug]/data-collection/energy-usage", { orgSlug }])}
        isActive={router.pathname.startsWith("/[orgSlug]/data-collection/energy-usage")}
        icon={IconBolt}
      >
        Energy forms
      </MenuItem>
      <MenuItem
        href={url(["/[orgSlug]/data-collection/forms", { orgSlug }])}
        isActive={router.pathname.startsWith("/[orgSlug]/data-collection/forms")}
        icon={IconList}
      >
        Qualification forms
      </MenuItem>
      <div className="flex-grow border-b border-brand-secondary-900" />
      <SidebarFooter />
    </div>
  )

  return (
    <>
      <SidebarWrapper open={open} onClose={onClose} className={className}>
        {content}
      </SidebarWrapper>
    </>
  )
}

const SidebarFooter = () => {
  const router = useRouter()
  const orgSlug = useOrgSlug()

  return (
    <>
      <div className="md:py-6">
        <MenuItem
          href={url([
            "/[orgSlug]/settings?tab=[tab]",
            { tab: SettingsTab.Users, orgSlug },
          ])}
          isActive={false}
          icon={IconSend}
        >
          Share with colleague
        </MenuItem>
        <MenuItem
          href={flaskUrl(["/help/product"])}
          isActive={false}
          icon={IconHelpOctagon}
        >
          Help center
        </MenuItem>
        <MenuItem
          href={url(["/[orgSlug]/settings", { orgSlug }])}
          isActive={router.pathname.startsWith("/[orgSlug]/settings")}
          icon={IconSettings}
        >
          Settings
        </MenuItem>
      </div>
    </>
  )
}

interface MenuItemProps {
  children: ReactNode
  href: LinkProps["href"]
  icon?: Icon
  isActive: boolean
}

const MenuItem = ({ children, href, icon, isActive }: MenuItemProps) => {
  const IconComp = icon

  return (
    <NavigationMenu.Item
      className={twMerge(
        "border-r-4 border-solid border-transparent focus-within:bg-brand-secondary-900 hover:bg-brand-secondary-900",
        { "border-r-brand-secondary-400 bg-brand-secondary-900": isActive },
      )}
    >
      <Link legacyBehavior href={href} passHref>
        <NavigationMenu.Link
          className={twMerge(
            "flex h-[52px] items-center gap-2 px-5 py-4 transition-all",
          )}
        >
          <div className="w-6">
            {IconComp && <IconComp size={24} strokeWidth={1} />}
          </div>
          <span className="whitespace-nowrap opacity-100 transition-all duration-300">
            {children}
          </span>
        </NavigationMenu.Link>
      </Link>
    </NavigationMenu.Item>
  )
}
